import React, { Fragment, memo } from 'react';

import { IconsEnum } from '../../../../assets/icons/types';

import { PrimaryNavTabsEnum } from '../../../hooks/useMenu/types';

import {
  useCompactWorkspaceSecondaryMenu,
  workspaceMenuLinks
} from '../../../../main/common/components/menus/CompactWorkspaceSecondaryMenu/hooks/useCompactWorkspaceSecondaryMenu';
import {
  paymentsMenuLinks,
  useCompactPaymentsSecondaryMenu
} from '../../../../main/common/components/menus/CompactPaymentsSecondaryMenu/hooks/useCompactPaymentsSecondaryMenu';
import { useCompactPeopleSecondaryMenu } from '../../../../main/common/components/menus/CompactPeopleSecondaryMenu/hooks/useCompactPeopleSecondaryMenu';
import { useCurrentUser } from '../../../../auth/hooks/useAuth';

import { CheckPermissions } from '../../../../helpers/CheckPermissions';
import { PrimaryNavTab } from './helpers/PrimaryNavTab';
import { PrimaryNavTooltipMenuTab } from './helpers/PrimaryNavTooltipMenuTab';
import { PrimaryNavLibraryTab } from './helpers/PrimaryNavLibraryTab';
import { TooltipSingletonSourceWrapper } from '../../../../helpers/tooltips/TooltipSingletonSourceWrapper';
import { TooltipPlacement } from '../../../../helpers/tooltips/tooltipsConstants';

import { ArchiAppPath } from '../../../../main/archiApps/ArchiAppPath';
import { InvoicePath } from '../../../../main/invoices/InvoicePath';
import { NotificationPath } from '../../../../main/notifications/NotificationPath';
import { PermissionPath } from '../../../../main/permissions/PermissionPath';
import { ProjectPath } from '../../../../main/projects/ProjectPath';
import { SupersetReportPath } from '../../../../main/supersetReports/SupersetReportPath';
import { TaskPath } from '../../../../main/tasks/TaskPath';
import { TeamPath } from '../../../../main/teams/TeamPath';
import { BookStackPath } from '../../../../main/bookStack/BookStackPath';
import { ProductPath } from '../../../../main/products/ProductPath';

import { menuKeys, tasksKeys } from '../../../../locales/keys';

import { AppPermissions } from '../../../appConstants';
import { stockMenuLinks } from '../../../../main/common/components/menus/CompactStockSecondaryMenu/compactStockSecondaryMenuConstants';
import { selfCompanyLibraryMenuLinks } from '../../../../main/common/components/menus/CompactCompanyLibraryMenu/compactCompanyLibraryMenuConstants';
import { toolsMenuLinks } from '../../../../main/common/components/menus/ToolsSecondaryMenu/hooks/useToolsSecondaryMenu';
import { TeamsPermissions } from '../../../../main/teams/teamsConstants';
import { aiStudioMenuLinks } from '../../../../main/archiApps/archiAppsConstants';

function PrimaryNav() {
  const { permissions: workspacePermissions } =
    useCompactWorkspaceSecondaryMenu();
  const { permissions: paymentsPermissions } =
    useCompactPaymentsSecondaryMenu();
  const { permissions: peoplePermissions, peopleMenuLinks } =
    useCompactPeopleSecondaryMenu();

  const currentUser = useCurrentUser();

  return (
    <Fragment>
      <TooltipSingletonSourceWrapper
        placement={TooltipPlacement.RIGHT}
        withArrow
      >
        <CheckPermissions action={AppPermissions.READ_MENU_NOTIFICATIONS_TAB}>
          <PrimaryNavTab
            dataGa="notifications-menu-link"
            href={NotificationPath.index()}
            icon={IconsEnum.BELL}
            tooltipI18nText={menuKeys.tabs.notifications}
            tab={PrimaryNavTabsEnum.NOTIFICATIONS}
          />
        </CheckPermissions>

        <CheckPermissions
          action={AppPermissions.READ_MENU_TASKS_AND_PROJECTS_TAB}
        >
          <PrimaryNavTab
            dataGa="workspace-menu-link"
            href={ProjectPath.index()}
            icon={IconsEnum.PROJECTS}
            tooltipI18nText={menuKeys.tabs.workspace}
            tab={PrimaryNavTabsEnum.TASKS_AND_PROJECTS}
          />
        </CheckPermissions>

        <CheckPermissions
          action={AppPermissions.READ_MENU_COMPACT_WORKSPACE_TAB}
        >
          <PrimaryNavTooltipMenuTab
            addClassName="workspace-menu-tour"
            href={TaskPath.compactIndex()}
            tooltipI18nText={menuKeys.tabs.workspace}
            tab={PrimaryNavTabsEnum.COMPACT_WORKSPACE}
            icon={IconsEnum.PROJECTS}
            tooltipLinks={workspaceMenuLinks(workspacePermissions)}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_RESULTS_TAB}>
          <PrimaryNavTab
            dataGa="results-menu-link"
            href={TaskPath.compactResults()}
            icon={IconsEnum.ROCKET_OUTLINE}
            tooltipI18nText={tasksKeys.results}
            tab={PrimaryNavTabsEnum.RESULTS}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_COMPANY_LIBRARY_TAB}>
          <PrimaryNavTooltipMenuTab
            href={TeamPath.currentCompanyLibraryProducts()}
            icon={IconsEnum.CUBE_OUTLINE}
            tab={PrimaryNavTabsEnum.COMPANY_LIBRARY}
            tooltipI18nText={menuKeys.tabs.myLibrary}
            tooltipLinks={selfCompanyLibraryMenuLinks}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_STOCK_TAB}>
          <PrimaryNavTooltipMenuTab
            addClassName="three-d-stock-menu-tour"
            href={ProductPath.index()}
            icon={IconsEnum.CUBES_OUTLINE}
            tab={PrimaryNavTabsEnum.STOCK}
            tooltipI18nText={menuKeys.tabs.stock3d}
            tooltipLinks={stockMenuLinks}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_NEW_STOCK_TAB}>
          <PrimaryNavLibraryTab />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_PEOPLE_TAB}>
          <PrimaryNavTab
            dataGa="people-menu-link"
            href={TeamPath.companiesMyCompanies()}
            icon={IconsEnum.USER_GROUP}
            tooltipI18nText={menuKeys.tabs.people}
            tab={PrimaryNavTabsEnum.PEOPLE}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_COMPACT_PEOPLE_TAB}>
          <PrimaryNavTooltipMenuTab
            href={
              currentUser.hasPermissions(
                TeamsPermissions.READ_PEOPLE_MENU_COMPANIES_GRID_LINK
              )
                ? TeamPath.companiesGrid()
                : TeamPath.companiesCompactMyCompanies()
            }
            icon={IconsEnum.USER_GROUP}
            tooltipI18nText={menuKeys.tabs.people}
            tab={PrimaryNavTabsEnum.PEOPLE}
            tooltipLinks={peopleMenuLinks(peoplePermissions)}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_PAYMENTS_TAB}>
          <PrimaryNavTooltipMenuTab
            addClassName="payments-menu-tour"
            href={InvoicePath.paymentsInvoices()}
            icon={IconsEnum.CASH_OUTLINE}
            tooltipI18nText={menuKeys.tabs.payments}
            tab={PrimaryNavTabsEnum.PAYMENTS}
            tooltipLinks={paymentsMenuLinks(paymentsPermissions)}
          />
        </CheckPermissions>
        <CheckPermissions action={AppPermissions.READ_MENU_REPORTS_TAB}>
          <PrimaryNavTab
            dataGa="reports-menu-link"
            href={SupersetReportPath.index()}
            icon={IconsEnum.DOCUMENT_REPORT_OUTLINE}
            tooltipI18nText={menuKeys.tabs.reports}
            tab={PrimaryNavTabsEnum.REPORTS}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_BOOKSTACK_TAB}>
          <PrimaryNavTab
            dataGa="bookstack-menu-link"
            href={BookStackPath.index()}
            icon={IconsEnum.BOOK_STACK_OUTLINE}
            tooltipI18nText={menuKeys.tabs.knowledgeLibrary}
            tab={PrimaryNavTabsEnum.BOOKSTACK}
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_TOOLS_TAB}>
          <PrimaryNavTooltipMenuTab
            dataGa="tools-menu-link"
            href={ArchiAppPath.index()}
            icon={IconsEnum.VIEW_GRID}
            tooltipI18nText={menuKeys.tabs.tools}
            tab={PrimaryNavTabsEnum.TOOLS}
            tooltipLinks={toolsMenuLinks}
            withoutSaveLastPath
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_AI_STUDIO_TAB}>
          <PrimaryNavTooltipMenuTab
            dataGa="ai-studio-menu-link"
            href={ArchiAppPath.aiStudio()}
            icon={IconsEnum.SPARKLES_OUTLINE}
            tooltipI18nText={menuKeys.tabs.aiStudio}
            tab={PrimaryNavTabsEnum.AI_STUDIO}
            tooltipLinks={aiStudioMenuLinks}
            withoutSaveLastPath
          />
        </CheckPermissions>

        <CheckPermissions action={AppPermissions.READ_MENU_ADMIN_TAB}>
          <PrimaryNavTab
            dataGa="admin-menu-link"
            href={PermissionPath.index()}
            icon={IconsEnum.SHIELD_EXCLAMATION}
            tooltipI18nText={menuKeys.tabs.admin}
            tab={PrimaryNavTabsEnum.ADMIN}
          />
        </CheckPermissions>
      </TooltipSingletonSourceWrapper>
    </Fragment>
  );
}

export default memo(PrimaryNav);
